/**
 * useScreenActions — ViewModel for /iam/screen-actions.
 *
 * Read-only: composes the shared usePaginatedList<T> hook (SOP 02
 * §"List Pagination") for the list. The flags are authored in code +
 * migrations, so there is no mutation path here.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import type { ScreenWithActions } from '../models/screen-action.model'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/screen-action.model.ts.
export type { ScreenWithActions } from '../models/screen-action.model'

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ScreenWithActions[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export interface UseScreenActionsReturn {
  readonly items: ScreenWithActions[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
}

export function useScreenActions(): UseScreenActionsReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<ScreenWithActions>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.IAMSCREENACTIONS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload
  }
}
